import { capture } from '@parcel/analytics/browser';
import {
  SIGNAL_TYPE_DEFINITIONS,
  type SignalType,
  groupSubscribableSignalTypesByStage,
} from '@parcel/types';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Checkbox,
  Field,
  FieldGroup,
  FieldLabel,
  Input,
  Skeleton,
  cn,
} from '@parcel/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, ArrowRight, Check, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { apiErrorMessage } from '../../lib/api.js';
import { useWorkspaceFilterOptions } from '../../lib/filter-options.js';
import { peekPendingRedirect, takePendingRedirect } from '../../lib/pending-redirect.js';
import { parseRecordParam } from '../../lib/record-detail.js';
import { useScores, useSeedScores } from '../../lib/scores.js';
import {
  buildBulkSubscriptionsBody,
  createSignalSubscriptionsBulk,
  deleteSignalSubscription,
} from '../../lib/signal-subscriptions.js';
import {
  completeWorkspaceOnboarding,
  getWorkspace,
  getWorkspaceEntryPath,
  getWorkspaceRedirectLocation,
  updateUserProfile,
} from '../../lib/workspaces.js';
import { PriorityDropdown } from '../signals/priority-dropdown.js';
import { ScoringAnswersForm } from './scoring-answers-form.js';
import {
  type ScoringAnswers,
  defaultScoringAnswers,
  hasCriteria,
  toSeedBody,
  validateScoringAnswers,
} from './scoring-answers.js';
import {
  type SignalSelections,
  countSelectedInGroup,
  initialSelections,
  setSelectionPriority,
  toggleSelection,
} from './signal-selection.js';

interface WelcomeFlowProps {
  workspaceSlug: string;
}

type Step = 'profile' | 'invite' | 'signals' | 'scoring' | 'finish';
const STEP_LABELS: Record<Step, string> = {
  profile: 'Profile',
  invite: 'Invite',
  signals: 'Signals',
  scoring: 'Scoring',
  finish: 'Finish',
};
// Seeding scores is for owners and admins; a member's flow has no Scoring step.
const ADMIN_STEPS: readonly Step[] = ['profile', 'invite', 'scoring', 'signals', 'finish'];
const MEMBER_STEPS: readonly Step[] = ['profile', 'invite', 'signals', 'finish'];

// Stage-grouped subscribable signal types (pure/deterministic; computed once).
const SIGNAL_TYPE_GROUPS = groupSubscribableSignalTypesByStage();

// Label + description lookup for a signal type.
const DEFINITION_BY_TYPE = new Map(
  SIGNAL_TYPE_DEFINITIONS.map((definition) => [definition.value, definition]),
);

const HEADINGS: Record<Step, string> = {
  profile: 'Set up your profile',
  invite: 'Invite your teammates',
  signals: 'Choose your signals',
  scoring: 'Set your market preferences',
  finish: 'Your workspace is ready',
};
const BLURBS: Record<Step, string> = {
  profile: 'Add the details your team will see in shared workspace activity.',
  invite: 'Paste teammate emails now, or skip this and invite people later from settings.',
  signals:
    'Pick the signal types your team tracks and set how much each one matters in your News feed. Recommended ones are pre-selected. You can fine-tune alerts later in settings.',
  scoring:
    'These answers set the criteria that score every project, account and contact. Parcel also uses them to make recommendations.',
  finish: 'Start from your News feed now, or continue setup later from workspace settings.',
};

/** What the last button opens when a dossier link is pending, by its resolved destination. */
export function pendingLabel(location: { pathname: string; search: string } | null): string | null {
  if (!location?.search) return null;
  if (location.pathname.endsWith('/spec')) return 'Open Spec';
  const record = parseRecordParam(new URLSearchParams(location.search).get('record'));
  if (record?.type === 'account') return 'Open the firm';
  if (record?.type === 'project') return 'Open the project';
  return location.pathname.endsWith('/projects') ? 'Open the projects' : null;
}
/** A step's actions: Back alone on the left, then the skip and the way forward on the right, forward last. */
function StepActions({ back, children }: { back: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      {back}
      <div className="ml-auto flex flex-wrap items-center gap-3">{children}</div>
    </div>
  );
}

/** The three scores' versions: unchanged since our own seed means no one else has edited them. */
const seedVersions = (list: readonly { key: string; version: number }[]) =>
  list
    .map((score) => `${score.key}:${score.version}`)
    .sort()
    .join();

export function WelcomeFlow({ workspaceSlug }: WelcomeFlowProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('profile');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [title, setTitle] = useState('');
  const [emails, setEmails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invitesCompleted, setInvitesCompleted] = useState(false);
  // Signal selections: presence = subscribed, value = chosen priority.
  const [selections, setSelections] = useState<SignalSelections>(() => initialSelections());
  // By step, so going back and answering differently never counts one twice.
  const skippedStepsRef = useRef(new Set<Step>());
  const completedRef = useRef(false);
  // What a Continue already saved, so Back then Continue only changes the difference.
  const savedSignalsRef = useRef(new Set<SignalType>());
  const ownSeedRef = useRef<{ answers: string; versions: string } | null>(null);
  const [answers, setAnswers] = useState<ScoringAnswers | null>(null);
  const [seeded, setSeeded] = useState(false);
  const queryClient = useQueryClient();
  const workspaceQuery = useQuery({
    queryKey: ['workspace', workspaceSlug],
    queryFn: () => getWorkspace(workspaceSlug),
  });
  const workspace = workspaceQuery.data;
  const canSeed = workspace?.role === 'owner' || workspace?.role === 'admin';
  const steps = workspace && !canSeed ? MEMBER_STEPS : ADMIN_STEPS;
  const stepIndex = steps.indexOf(step);
  const scoringId = step === 'scoring' ? workspace?.id : undefined;
  const cities = useWorkspaceFilterOptions(scoringId);
  const cityNames = useMemo(() => cities.data?.cities.map((city) => city.value), [cities.data]);
  const scores = useScores(scoringId);
  // Criteria set in Settings (or by Spec) are never overwritten from here.
  // Our own seed fills the scores too; that must not read as criteria someone else set.
  const alreadyScored = hasCriteria(scores.data) && !submitting && !seeded;
  const seed = useSeedScores(workspace?.id);

  useEffect(() => {
    if (step === 'scoring' && answers === null && cityNames && scores.data) {
      setAnswers(defaultScoringAnswers(cityNames));
    }
  }, [step, answers, cityNames, scores.data]);

  const canContinueProfile = firstName.trim().length > 0 && lastName.trim().length > 0;

  const previousStep = steps[stepIndex - 1];
  const backButton = previousStep ? (
    <Button
      type="button"
      variant="ghost"
      className="-ml-3"
      disabled={submitting}
      onClick={() => {
        setError(null);
        setStep(previousStep);
      }}
    >
      <ArrowLeft className="size-4" />
      Back
    </Button>
  ) : null;

  const markSkipped = (name: Step, skipped: boolean) => {
    if (skipped) skippedStepsRef.current.add(name);
    else skippedStepsRef.current.delete(name);
  };
  const captureCompleted = () => {
    if (completedRef.current) return;
    completedRef.current = true;
    capture('welcome_completed', { steps_skipped_count: skippedStepsRef.current.size });
  };

  const saveProfile = async () => {
    setError(null);
    setSubmitting(true);

    try {
      await updateUserProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        title: title.trim(),
      });
      capture('welcome_step_completed', { step: 'profile', step_index: 0 });
      setStep('invite');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to save your profile.');
    } finally {
      setSubmitting(false);
    }
  };

  const continueFromInvite = async (completed: boolean) => {
    setInvitesCompleted(completed);
    setError(null);
    setSubmitting(true);
    try {
      // The Scoring step reads the workspace from this query; a direct read fills it.
      const current = workspace ?? (await getWorkspace(workspaceSlug));
      if (!workspace) queryClient.setQueryData(['workspace', workspaceSlug], current);
      setStep(current.role === 'owner' || current.role === 'admin' ? 'scoring' : 'signals');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to open the next step.');
    } finally {
      setSubmitting(false);
    }
  };

  const finishSignals = async (skipSignals: boolean) => {
    setError(null);
    setSubmitting(true);
    try {
      const current = workspace ?? (await getWorkspace(workspaceSlug));
      // Both bypasses skip the create: skipSignals = explicit "Set up later",
      // size === 0 = user cleared every checkbox. Bulk-create is idempotent
      // (re-running never resets queue watermarks), so retrying after a
      // failure further down is safe.
      try {
        if (!skipSignals && selections.size > 0) {
          await createSignalSubscriptionsBulk(current.id, buildBulkSubscriptionsBody(selections));
          for (const signalType of selections.keys()) savedSignalsRef.current.add(signalType);
        }
        // Back, then unticked: what the last Continue subscribed comes off, even on "Set up later".
        for (const signalType of [...savedSignalsRef.current]) {
          if (selections.has(signalType)) continue;
          await deleteSignalSubscription(current.id, signalType);
          savedSignalsRef.current.delete(signalType);
        }
      } catch {
        throw new Error('Unable to save your signal selections. Try again.');
      }
      // The last step: onboarding completes here.
      await completeWorkspaceOnboarding(workspaceSlug, {
        profileCompleted: true,
        invitesCompleted,
        completed: true,
      });
      markSkipped('signals', skipSignals);
      capture(skipSignals ? 'welcome_step_skipped' : 'welcome_step_completed', {
        step: 'signals',
        step_index: stepIndex,
      });
      captureCompleted();
      setStep('finish');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to finish workspace setup.');
    } finally {
      setSubmitting(false);
    }
  };

  const finishScoring = async (skipScoring: boolean) => {
    setError(null);
    const seeding = !skipScoring && !alreadyScored;
    if (seeding) {
      const problem = answers ? validateScoringAnswers(answers) : null;
      if (problem || !answers) {
        setError(problem);
        return;
      }
    }
    setSubmitting(true);
    try {
      // Back from Finish with the same answers: the seed already wrote them.
      if (seeding && answers && ownSeedRef.current?.answers !== JSON.stringify(answers)) {
        // Read the scores once more: criteria saved meanwhile (Settings, Spec) are never overwritten.
        const fresh = await scores.refetch();
        const ownSeed = fresh.data && ownSeedRef.current?.versions === seedVersions(fresh.data);
        if (fresh.isError || (hasCriteria(fresh.data) && !ownSeed)) {
          setError(
            fresh.isError
              ? "Couldn't check your scores. Try again, or set this up later."
              : 'Your scores already have criteria now, so nothing was changed.',
          );
          return;
        }
        const written = await seed.mutateAsync(toSeedBody(answers));
        ownSeedRef.current = {
          answers: JSON.stringify(answers),
          versions: seedVersions(written),
        };
        setSeeded(true);
      }
      if (!alreadyScored) {
        markSkipped('scoring', skipScoring);
        capture(skipScoring ? 'welcome_step_skipped' : 'welcome_step_completed', {
          step: 'scoring',
          step_index: 2,
        });
      }
      setStep('signals');
    } catch (err) {
      setError(
        apiErrorMessage(
          err,
          'Unable to save your scoring answers. Try again, or set this up later.',
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="flex min-h-svh items-center justify-center bg-muted p-6 md:p-10">
      <div className="w-full max-w-xl space-y-6">
        <header className="space-y-6 text-center">
          <div className="inline-flex">
            <img
              src="/brand/parcel-logo-black.svg"
              alt="Parcel"
              width="84"
              height="24"
              decoding="async"
              className="dark:hidden"
            />
            <img
              src="/brand/parcel-logo-white.svg"
              alt="Parcel"
              width="84"
              height="24"
              decoding="async"
              className="hidden dark:block"
            />
          </div>
          <div className="flex justify-center gap-2" aria-label="Setup progress">
            {steps.map((name, index) => (
              <span
                key={name}
                aria-label={STEP_LABELS[name]}
                className={cn(
                  'size-2.5 rounded-full transition-colors',
                  index <= stepIndex ? 'bg-brand' : 'bg-border',
                )}
              />
            ))}
          </div>
        </header>

        <div className="space-y-3 text-center">
          <p className="font-medium text-muted-foreground text-sm">
            {workspaceSlug} workspace setup
          </p>
          <h1 className="font-semibold text-3xl tracking-normal">
            {step === 'scoring' && alreadyScored ? 'Your scores are set' : HEADINGS[step]}
          </h1>
          <p className="mx-auto max-w-md text-muted-foreground text-sm leading-6">
            {step === 'scoring' && alreadyScored
              ? 'Parcel already ranks your projects, accounts and contacts with the criteria in Settings, Scoring.'
              : BLURBS[step]}
          </p>
        </div>

        <section className="rounded-lg border bg-background p-6 shadow-sm">
          <div className="space-y-6">
            {step === 'profile' ? (
              <form
                className="space-y-6"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (canContinueProfile && !submitting) void saveProfile();
                }}
              >
                <FieldGroup className="gap-5">
                  <Field>
                    <FieldLabel htmlFor="profile-first-name">First name</FieldLabel>
                    <Input
                      id="profile-first-name"
                      value={firstName}
                      onChange={(event) => setFirstName(event.target.value)}
                      placeholder="Nina"
                      autoComplete="given-name"
                      required
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="profile-last-name">Last name</FieldLabel>
                    <Input
                      id="profile-last-name"
                      value={lastName}
                      onChange={(event) => setLastName(event.target.value)}
                      placeholder="Chen"
                      autoComplete="family-name"
                      required
                    />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="profile-title">Title</FieldLabel>
                    <Input
                      id="profile-title"
                      value={title}
                      onChange={(event) => setTitle(event.target.value)}
                      placeholder="Development manager"
                      autoComplete="organization-title"
                    />
                  </Field>
                </FieldGroup>
                {error ? <p className="text-sm text-destructive">{error}</p> : null}
                <StepActions back={backButton}>
                  <Button type="submit" disabled={!canContinueProfile || submitting}>
                    {submitting ? (
                      <>
                        <Loader2 className="size-4 animate-spin" />
                        Saving
                      </>
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="size-4" />
                      </>
                    )}
                  </Button>
                </StepActions>
              </form>
            ) : null}

            {step === 'invite' ? (
              <form
                className="space-y-6"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (submitting) return;
                  const hasEmails = emails.trim().length > 0;
                  markSkipped('invite', !hasEmails);
                  capture(hasEmails ? 'welcome_step_completed' : 'welcome_step_skipped', {
                    step: 'invite',
                    step_index: 1,
                  });
                  void continueFromInvite(hasEmails);
                }}
              >
                <Field>
                  <FieldLabel htmlFor="invite-emails">Teammate emails</FieldLabel>
                  <textarea
                    id="invite-emails"
                    value={emails}
                    onChange={(event) => setEmails(event.target.value)}
                    placeholder="alex@example.com, priya@example.com"
                    className="min-h-36 w-full resize-y rounded-md border bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                </Field>
                {error ? <p className="text-sm text-destructive">{error}</p> : null}
                <StepActions back={backButton}>
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={submitting}
                    onClick={() => {
                      markSkipped('invite', true);
                      capture('welcome_step_skipped', { step: 'invite', step_index: 1 });
                      void continueFromInvite(false);
                    }}
                  >
                    Skip
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? (
                      <>
                        <Loader2 className="size-4 animate-spin" />
                        Saving
                      </>
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="size-4" />
                      </>
                    )}
                  </Button>
                </StepActions>
              </form>
            ) : null}

            {step === 'signals' ? (
              <div className="space-y-6">
                <Accordion
                  type="multiple"
                  defaultValue={['pre_filing']}
                  className="rounded-lg border text-left"
                >
                  {SIGNAL_TYPE_GROUPS.map((group) => {
                    const selectedCount = countSelectedInGroup(selections, group);
                    return (
                      <AccordionItem key={group.key} value={group.key} className="last:border-b-0">
                        <AccordionTrigger className="gap-2 px-3 py-3 hover:bg-accent/50">
                          <span className="flex-1 text-left font-medium text-sm">
                            {group.label}
                          </span>
                          {selectedCount > 0 ? (
                            <span className="rounded-md border bg-muted px-1.5 py-0.5 font-medium text-muted-foreground text-xs">
                              {selectedCount}
                            </span>
                          ) : null}
                        </AccordionTrigger>
                        <AccordionContent className="px-1 pb-1">
                          {group.signalTypes.map((signalType) => {
                            const definition = DEFINITION_BY_TYPE.get(signalType);
                            const selected = selections.has(signalType);
                            return (
                              <div
                                key={signalType}
                                className="flex items-start gap-3 rounded-md px-2 py-2 hover:bg-muted/50"
                              >
                                <Checkbox
                                  id={`signal-type-${signalType}`}
                                  className="mt-0.5"
                                  checked={selected}
                                  onCheckedChange={(checked) =>
                                    setSelections((current) =>
                                      toggleSelection(current, signalType, checked === true),
                                    )
                                  }
                                />
                                <label
                                  htmlFor={`signal-type-${signalType}`}
                                  className="min-w-0 flex-1 cursor-pointer"
                                >
                                  <span className="block font-medium text-sm">
                                    {definition?.label ?? signalType}
                                  </span>
                                  {definition ? (
                                    <span className="block text-muted-foreground text-xs">
                                      {definition.description}
                                    </span>
                                  ) : null}
                                </label>
                                {/* Keep the priority control a sibling of the label, never nested
                                    inside it, so clicking it does not toggle the checkbox. */}
                                {selected ? (
                                  <PriorityDropdown
                                    value={selections.get(signalType) ?? 'medium'}
                                    onChange={(priority) =>
                                      setSelections((current) =>
                                        setSelectionPriority(current, signalType, priority),
                                      )
                                    }
                                  />
                                ) : null}
                              </div>
                            );
                          })}
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
                {error ? <p className="text-destructive text-sm">{error}</p> : null}
                <StepActions back={backButton}>
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={submitting}
                    onClick={() => void finishSignals(true)}
                  >
                    Set up later
                  </Button>
                  <Button
                    type="button"
                    disabled={submitting}
                    onClick={() => void finishSignals(false)}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="size-4 animate-spin" />
                        Saving
                      </>
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="size-4" />
                      </>
                    )}
                  </Button>
                </StepActions>
              </div>
            ) : null}

            {step === 'scoring' ? (
              <div className="space-y-6">
                {alreadyScored ? (
                  <p className="text-muted-foreground text-sm">
                    Your scores already have criteria, so there is nothing to answer here.
                  </p>
                ) : answers ? (
                  <ScoringAnswersForm
                    answers={answers}
                    cities={cityNames ?? []}
                    disabled={submitting}
                    onChange={setAnswers}
                  />
                ) : cities.isError || scores.isError ? (
                  <p className="text-muted-foreground text-sm">
                    Couldn't load your scoring. Set this up later from Settings, Scoring.
                  </p>
                ) : (
                  <Skeleton className="h-64 w-full" />
                )}
                {error ? <p className="text-destructive text-sm">{error}</p> : null}
                <StepActions back={backButton}>
                  {alreadyScored ? null : (
                    <Button
                      type="button"
                      variant="ghost"
                      disabled={submitting}
                      onClick={() => void finishScoring(true)}
                    >
                      Set up later
                    </Button>
                  )}
                  <Button
                    type="button"
                    disabled={submitting || (!answers && !alreadyScored)}
                    onClick={() => void finishScoring(false)}
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="size-4 animate-spin" />
                        Scoring your book
                      </>
                    ) : (
                      <>
                        Continue
                        <ArrowRight className="size-4" />
                      </>
                    )}
                  </Button>
                </StepActions>
              </div>
            ) : null}

            {step === 'finish' ? (
              <div className="space-y-6">
                <div className="grid gap-3 rounded-lg border bg-muted/40 p-5">
                  <div className="flex items-center gap-3">
                    <span className="flex size-8 items-center justify-center rounded-full bg-emerald-500 text-white">
                      <Check className="size-4" />
                    </span>
                    <p className="font-medium">Workspace setup saved.</p>
                  </div>
                  <p className="text-muted-foreground text-sm">
                    {seeded
                      ? 'Your scores are set, so your News feed has suggestions ready.'
                      : 'Your profile and workspace onboarding status are ready.'}
                  </p>
                </div>
                <StepActions back={backButton}>
                  <Button
                    onClick={() => {
                      // A dossier link the signup started from: open its record, once.
                      const pending = takePendingRedirect();
                      if (!pending)
                        return void navigate({ to: getWorkspaceEntryPath(workspaceSlug) });
                      const location = getWorkspaceRedirectLocation(workspaceSlug, pending);
                      // A query string (a record to open) needs a full load, as the workspace picker does.
                      if (location.search) window.location.assign(location.href);
                      else void navigate({ to: location.pathname });
                    }}
                  >
                    {pendingLabel(
                      peekPendingRedirect()
                        ? getWorkspaceRedirectLocation(workspaceSlug, peekPendingRedirect())
                        : null,
                    ) ?? (seeded ? 'Open the News feed' : 'Enter app')}
                    <ArrowRight className="size-4" />
                  </Button>
                </StepActions>
              </div>
            ) : null}
          </div>
        </section>
      </div>
    </main>
  );
}
